import { useInfiniteQuery } from '@tanstack/react-query'
import { Empty, Flex, Select, Spin } from 'antd'
import { SelectProps } from 'antd/lib'
import { useState } from 'react'

import { useStocksServiceGetListFundingSourceEndpointKey } from 'src/api/queries'
import { GetGroupListResponseItem, StocksService } from 'src/api/requests'
import { DEFAULT_PER_PAGE } from 'src/app-constants'
import { useDebounce } from 'src/hooks'
import { useLocales } from 'src/locales'

export const FundingSourceSelect = (props: SelectProps) => {
  const { t, currentLang } = useLocales()
  const [search, setSearch] = useState('')
  const debouncedSearch = useDebounce(search)

  const { data, fetchNextPage, isFetching } = useInfiniteQuery({
    queryKey: [
      useStocksServiceGetListFundingSourceEndpointKey,
      debouncedSearch
    ],
    queryFn: async ({ pageParam = 1 }: { pageParam?: number }) => {
      const response = await StocksService.getListFundingSourceEndpoint(
        pageParam,
        DEFAULT_PER_PAGE,
        debouncedSearch
      )

      return {
        data: response?.data ?? [],
        total_pages: response.totalPages ?? 0,
        current_page: pageParam
      }
    },
    getNextPageParam: (lastPage: {
      data: GetGroupListResponseItem[]
      total_pages: number
      current_page: number
    }) =>
      lastPage?.current_page < lastPage?.total_pages
        ? lastPage.current_page + 1
        : undefined,
    initialPageParam: 1
  })

  const options = data?.pages.flatMap(
    (page) =>
      page.data?.map((item: GetGroupListResponseItem) => ({
        value: item.id,
        label: item?.name?.[currentLang?.value]
      })) || []
  )

  return (
    <Select
      allowClear
      popupMatchSelectWidth={true}
      filterOption={false}
      options={options}
      onSearch={(value) => setSearch(value)}
      onClear={() => setSearch('')}
      className="full-width"
      loading={isFetching}
      placeholder={t('menu.funding_source')}
      showSearch={true}
      onPopupScroll={(e) => {
        const target = e.target as HTMLElement
        if (target.scrollTop + target.offsetHeight === target.scrollHeight)
          fetchNextPage()
      }}
      notFoundContent={
        props.loading ? (
          <Flex justify="center">
            <Spin />
          </Flex>
        ) : (
          <Empty />
        )
      }
      {...props}
    />
  )
}
